import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, Building2, Layers, ShieldCheck, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERIODS = [
  { id: 'this_month', label: 'This Month' },
  { id: 'this_quarter', label: 'This Quarter' },
  { id: 'this_year', label: 'This Year' },
  { id: 'ytd', label: 'YTD' },
  { id: 'all_time', label: 'All Time' },
];

const DEP_COLORS = {
  cloud_saas: 'bg-sky-500',
  on_premise: 'bg-slate-500',
  customer_cloud: 'bg-indigo-500',
  hybrid: 'bg-purple-500',
  unknown: 'bg-slate-300',
};

const DEP_LABELS = {
  cloud_saas: 'Cloud SaaS',
  on_premise: 'On-Premises',
  customer_cloud: 'Customer Cloud',
  hybrid: 'Hybrid',
  unknown: 'Unknown',
};

function fmtINR(v) {
  if (!v && v !== 0) return '—';
  const n = Number(v);
  if (n >= 1e7) return '₹' + (n / 1e7).toFixed(2) + ' Cr';
  if (n >= 1e5) return '₹' + (n / 1e5).toFixed(2) + ' L';
  return '₹' + n.toLocaleString('en-IN');
}

function StatCard({ label, value, hint, icon: Icon, color = 'text-blue-600' }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</p>
          <p className={'text-2xl font-bold mt-1 ' + color}>{value}</p>
          {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
        </div>
        {Icon && <Icon className="w-5 h-5 text-slate-300 dark:text-slate-600 flex-shrink-0 ml-2" />}
      </div>
    </div>
  );
}

function BarRow({ label, value, max, color, count }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="font-medium text-slate-700 dark:text-slate-200">{label}</span>
        <span className="text-slate-600 dark:text-slate-300">{fmtINR(value)}{count != null && <span className="ml-2 text-slate-400">· {count} deal{count === 1 ? '' : 's'}</span>}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <div className={'h-full rounded-full transition-all ' + color} style={{ width: pct + '%' }}></div>
      </div>
    </div>
  );
}

export default function RevenueMixReport() {
  const [period, setPeriod] = useState('ytd');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/reports/revenue-mix?period=${period}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load revenue mix'))
      .finally(() => setLoading(false));
  }, [period]);

  const t = data?.totals || {};
  const mix = data?.mix || {};
  const maxDep = Math.max(1, ...(data?.by_deployment || []).map(x => x.revenue));
  const maxCom = Math.max(1, ...(data?.by_commercial_model || []).map(x => x.revenue));

  return (
    <div className="p-6 space-y-5" data-testid="revenue-mix-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-green-500" /> Revenue Mix
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Won-deal revenue broken down by deployment, commercial model, and AMC renewal schedule.</p>
        </div>
        <div className="flex gap-1 flex-wrap">
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)} className={'text-xs px-3 py-1.5 rounded-full transition-colors ' + (period === p.id ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200')} data-testid={`period-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div></div>
      ) : !data ? (
        <p className="text-center text-slate-400 py-16">No data.</p>
      ) : data.won_deal_count === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="revenue-empty">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No won deals in this period yet.</p>
          <p className="text-[11px] mt-1">Revenue reports populate once opportunities with SKU line items move to <b>Closed Won</b>.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total Revenue" value={fmtINR(t.revenue)} hint={`${data.won_deal_count} won deal${data.won_deal_count === 1 ? '' : 's'}`} icon={TrendingUp} color="text-green-600 dark:text-green-400" />
            <StatCard label="License" value={fmtINR(t.license)} icon={Layers} color="text-blue-600 dark:text-blue-400" />
            <StatCard label="AMC — Year 1" value={fmtINR(t.amc_y1)} hint={`${fmtINR(t.amc_recurring_annual)} recurring/yr`} icon={ShieldCheck} color="text-amber-600 dark:text-amber-400" />
            <StatCard label="Professional Services" value={fmtINR(t.professional_services)} icon={Building2} color="text-purple-600 dark:text-purple-400" />
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Revenue by Deployment</h3>
              <div className="space-y-3">
                {data.by_deployment.length === 0 ? <p className="text-xs text-slate-400">No deployment data yet.</p> : data.by_deployment.map(x => (
                  <BarRow key={x.deployment_type} label={DEP_LABELS[x.deployment_type] || x.deployment_type} value={x.revenue} max={maxDep} color={DEP_COLORS[x.deployment_type] || 'bg-slate-400'} count={x.deal_count} />
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">CapEx vs OpEx Mix</h3>
              <p className="text-[11px] text-slate-500 mb-4">Commercial model breakdown of won revenue.</p>
              <div className="space-y-3">
                {data.by_commercial_model.length === 0 ? <p className="text-xs text-slate-400">No commercial data yet.</p> : data.by_commercial_model.map(x => (
                  <BarRow key={x.commercial_model} label={x.commercial_model.toUpperCase()} value={x.revenue} max={maxCom} color={x.commercial_model === 'capex' ? 'bg-orange-500' : x.commercial_model === 'opex' ? 'bg-emerald-500' : 'bg-purple-500'} count={x.deal_count} />
                ))}
              </div>
              {(mix.capex_pct > 0 || mix.opex_pct > 0) && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex-1 text-center bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2">
                    <p className="text-[11px] text-orange-700 dark:text-orange-300 font-medium">CapEx</p>
                    <p className="text-lg font-bold text-orange-700 dark:text-orange-300">{mix.capex_pct}%</p>
                  </div>
                  <div className="flex-1 text-center bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2">
                    <p className="text-[11px] text-emerald-700 dark:text-emerald-300 font-medium">OpEx</p>
                    <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{mix.opex_pct}%</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5" data-testid="amc-renewal-pipeline">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-500" /> AMC Renewal Pipeline</h3>
              <span className="text-[11px] text-slate-500">{data.amc_renewal_pipeline.length} upcoming renewal{data.amc_renewal_pipeline.length === 1 ? '' : 's'}</span>
            </div>
            {data.amc_renewal_pipeline.length === 0 ? (
              <p className="text-xs text-slate-400">No recurring AMC lines in won deals for this period.</p>
            ) : (
              <div className="space-y-2">
                {data.amc_renewal_pipeline.map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-900/30 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 dark:text-white truncate">{p.opportunity_name}</p>
                      <p className="text-[11px] text-slate-500">{p.addon_name}{p.period ? ' · ' + p.period : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-amber-600 dark:text-amber-400">{fmtINR(p.amount)}</p>
                      <p className="text-[11px] text-slate-400">{p.next_renewal_at ? new Date(p.next_renewal_at).toLocaleDateString('en-IN') : '—'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
